import React, { useContext, useMemo } from 'react';
import clsx from 'clsx';
import { useReactlytics } from '@americanexpress/reactlytics';
import { useDispatch } from 'react-redux';
import { sanitize } from '@americanexpress/consumer-travel-base';
import { Button } from '@americanexpress/dls-react';
import { useExperimentContext } from '@americanexpress/one-experiments';
import htmlUnescape from '../../utils/htmlUnescape';
import styles from './Suggestions.scss';
import locationContext from '../../context/locationsContext';
import { locationShape } from '../../shape/locationShape';
import { RECENT_SEARCHES_KEY } from '../../constants/categoryConstants';
import { getAnalyticsUtils } from '../../utils/getAnalyticsUtils';
import { postContextFeedbackThunk } from '../../thunks/postContextFeedbackThunk';
import { useRecentSearchAPI, useRecentSearchData } from '../../hooks/apiHooks';
import { HOMEPAGE_REDESIGN_EXPERIMENT } from '../../constants/experimentConstants';

const SuggestionItem = ({
  id, name, label, airportCode, parentId, type,
  geoLocation, searchIdType, suggestionIndex,
  toLabel, toAirportCode, dates,
}) => {
  const {
    state: {
      location,
      focusedLocationRef,
      isRoundTrip,
    },
    actions: {
      selectLocation,
    },
    config: {
      lineOfBusiness,
    },
  } = useContext(locationContext);
  const printedName = `${parentId ? '> ' : ''}${name}`;
  const { track } = useReactlytics();
  const dispatch = useDispatch();
  const recentSearchData = useRecentSearchData();
  const { trackingId } = useRecentSearchAPI();

  const handleSelect = () => {
    selectLocation({
      id,
      name,
      label,
      airportCode,
      parentId,
      type,
      geoLocation,
      toLabel,
      toAirportCode,
      dates,
      searchIdType,
    });
  };

  const triggerAnalytics = () => {
    track({
      event: {
        element: getAnalyticsUtils({
          type,
          lob: lineOfBusiness,
          isRoundTrip,
          suggestionIndex,
        }),
        detail: 'recent-search-selected',
        type: 'click',
      },
    });
  };
  const onClick = () => {
    handleSelect();
    if (type === RECENT_SEARCHES_KEY) {
      triggerAnalytics();
      dispatch(postContextFeedbackThunk({
        recentSearchData,
        eventType: 'click',
        lob: lineOfBusiness,
        suggestionIndex,
        trackingId,
      }));
    }
  };

  const { selectors: { getExperimentVariant } } = useExperimentContext();
  const experimentData = getExperimentVariant(HOMEPAGE_REDESIGN_EXPERIMENT);

  const isVariantRedesign = useMemo(() => experimentData
    && experimentData.dimension === 'search_widget'
    && experimentData.variant !== 'control', [experimentData?.dimension, experimentData?.variant]);

  return (
    <Button
      variant="tertiary"
      className={clsx(
        'flex-align-center text-align-left width-full fluid pad-0-r',
        isVariantRedesign ? 'text-wrap pad-1-tb' : `${styles.suggestion} pad-4-l pad-0-tb`,
        { 'focus-light': focusedLocationRef === suggestionIndex }
      )}
      key={suggestionIndex}
      role="option"
      id={`suggestion-${suggestionIndex}`}
      aria-selected={id === location?.id}
      aria-current={id === location?.id}
      onClick={onClick}
      onKeyDown={handleSelect}
      tabIndex={-1}
    >
      <p
        className={clsx({ 'pad-1-l': parentId }, isVariantRedesign ? 'margin-2-l margin-1-r font-weight-normal' : 'body-1')}
        // eslint-disable-next-line react/no-danger -- value from oneCMS
        dangerouslySetInnerHTML={{ __html: htmlUnescape(sanitize(printedName)) }}
      />
    </Button>
  );
};

SuggestionItem.propTypes = locationShape;

export { SuggestionItem as TestableSuggestion };
export default SuggestionItem;
